import React, { useEffect, useMemo, useRef, useState } from "react";
import { Blvd, PlatformTarget } from "@boulevard/blvd-book-sdk";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ChevronLeft, Check, Calendar, Clock, MapPin, CreditCard, User } from "lucide-react";

const money = (cents) =>
  typeof cents === "number" ? `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}` : null;

const priceLabel = (item) => {
  const r = item?.listPriceRange;
  if (!r || typeof r.min !== "number") return null;
  if (r.variable || (typeof r.max === "number" && r.max !== r.min)) return `from ${money(r.min)}`;
  return money(r.min);
};

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return String(iso);
  }
};

export default function BoulevardBooking({ apiKey, businessId, env, preselect, prefill, onClose }) {
  const client = useMemo(() => {
    const target = env === "sandbox" ? PlatformTarget.Sandbox : PlatformTarget.Live;
    return new Blvd(apiKey, businessId, target);
  }, [apiKey, businessId, env]);

  const [step, setStep] = useState("loading"); // loading | service | datetime | details | payment | done | error
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const cartRef = useRef(null);
  const locationRef = useRef(null);
  const [locationName, setLocationName] = useState("");
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null); // staff variant or null = no preference

  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // CartBookableDate obj
  const [times, setTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null); // CartBookableTime obj

  const [form, setForm] = useState({
    firstName: prefill?.firstName || "",
    lastName: prefill?.lastName || "",
    email: prefill?.email || "",
    phoneNumber: prefill?.phoneNumber || "",
  });
  const [card, setCard] = useState({ name: "", number: "", cvv: "", exp_month: "", exp_year: "", address_postal_code: "" });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const business = await client.businesses.get();
        const locations = await business.getLocations();
        if (!locations?.length) throw new Error("No booking locations available.");
        const location = locations[0];
        const cart = await client.carts.create(location);
        cartRef.current = cart;
        locationRef.current = location;
        if (cancelled) return;
        setLocationName(location.name || "");
        const cats = await cart.getAvailableCategories();
        const flat = (cats || []).flatMap((c) =>
          (c.availableItems || [])
            .filter((it) => !it.disabled)
            .map((it) => ({ ref: it, id: it.id, name: it.name, category: c.name, price: priceLabel(it) }))
        );
        if (cancelled) return;
        setItems(flat);
        if (preselect) {
          const p = preselect.toLowerCase();
          const match = flat.find(
            (it) => it.name?.toLowerCase() === p || it.name?.toLowerCase().includes(p) || p.includes(it.name?.toLowerCase())
          );
          if (match) {
            await chooseItem(match);
            return;
          }
        }
        setStep("service");
      } catch (e) {
        if (!cancelled) {
          setError(e?.message?.includes("No booking") ? e.message : "We couldn't load live availability right now. Please try again in a moment.");
          setStep("error");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const staffName = (v) => {
    const s = v?.staff;
    if (!s) return "Any available provider";
    return `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Provider";
  };

  async function chooseItem(item) {
    setBusy(true);
    setError("");
    try {
      setSelectedItem(item);
      let variants = [];
      try { variants = await item.ref.getStaffVariants(); } catch {}
      setProviders(variants || []);
      if (!variants || variants.length <= 1) {
        await addItemWithProvider(item, variants && variants.length === 1 ? variants[0] : null);
      } else {
        setStep("provider");
      }
    } catch (e) {
      setError("That service isn't available for online booking. Please pick another.");
      setStep("service");
    } finally {
      setBusy(false);
    }
  }

  async function addItemWithProvider(item, variant) {
    // clear any previously selected item so navigating back doesn't double-book
    try {
      const selected = await cartRef.current.getSelectedItems();
      for (const s of selected || []) {
        cartRef.current = await cartRef.current.removeSelectedItem(s);
      }
    } catch {}
    const next = await cartRef.current.addBookableItem(item.ref, variant ? { staffVariant: variant } : undefined);
    cartRef.current = next || cartRef.current;
    setSelectedProvider(variant || null);
    const d = await cartRef.current.getBookableDates();
    setDates(d || []);
    setSelectedDate(null);
    setTimes([]);
    setSelectedTime(null);
    setStep("datetime");
  }

  async function chooseProvider(variant) {
    setBusy(true);
    setError("");
    try {
      await addItemWithProvider(selectedItem, variant);
    } catch (e) {
      setError("That provider isn't available for online booking. Please try another.");
    } finally {
      setBusy(false);
    }
  }

  async function chooseDate(d) {
    setBusy(true);
    setError("");
    try {
      setSelectedDate(d);
      setSelectedTime(null);
      const t = await cartRef.current.getBookableTimes(d);
      setTimes(t || []);
    } catch (e) {
      setError("Couldn't load times for that day.");
    } finally {
      setBusy(false);
    }
  }

  async function goToDetails() {
    if (!selectedTime) return;
    setBusy(true);
    setError("");
    try {
      const next = await cartRef.current.reserveBookableItems(selectedTime);
      cartRef.current = next || cartRef.current;
      setStep("details");
    } catch (e) {
      setError("That time was just taken. Please choose another slot.");
      if (selectedDate) {
        try {
          const t = await cartRef.current.getBookableTimes(selectedDate);
          setTimes(t || []);
          setSelectedTime(null);
        } catch {}
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitDetails(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const updated = await cartRef.current.update({
        clientInformation: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
        },
      });
      cartRef.current = updated || cartRef.current;
      const s = cartRef.current.summary;
      setSummary(s);
      if (s?.paymentMethodRequired) setStep("payment");
      else await finalize();
    } catch (err) {
      setError("Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitPayment(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await cartRef.current.addCardPaymentMethod({
        card: {
          name: card.name.trim(),
          number: card.number.replace(/\s+/g, ""),
          cvv: card.cvv.trim(),
          exp_month: parseInt(card.exp_month, 10),
          exp_year: parseInt(card.exp_year, 10),
          address_postal_code: card.address_postal_code.trim(),
        },
      });
      await finalize();
    } catch (err) {
      setError("Your card couldn't be processed. Please double-check the details.");
    } finally {
      setBusy(false);
    }
  }

  async function finalize() {
    await cartRef.current.checkout();
    setStep("done");
  }

  const Header = ({ title, sub, back }) => (
    <div className="mb-5">
      {back && (
        <button onClick={back} disabled={busy} className="inline-flex items-center gap-1 text-sm text-[#5C6664] hover:text-ink mb-3 transition-colors" data-testid="blvd-back-button">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
      )}
      <h3 className="font-heading text-xl font-light tracking-tight text-ink">{title}</h3>
      {sub && <p className="text-sm text-[#5C6664] mt-1">{sub}</p>}
    </div>
  );

  const Spinner = () => (
    <div className="flex items-center justify-center py-12" data-testid="blvd-loading">
      <Loader2 className="h-6 w-6 animate-spin text-iri-mid" />
    </div>
  );

  return (
    <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto" data-testid="blvd-booking">
      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-sm text-rose-700" data-testid="blvd-error">
          {error}
        </div>
      )}

      {step === "loading" && (
        <>
          <Header title="Loading availability" sub="Fetching live openings from Boulevard…" />
          <Spinner />
        </>
      )}

      {step === "error" && (
        <>
          <Header title="Booking is briefly unavailable" />
          <p className="text-sm text-[#5C6664]">{error}</p>
          <Button className="mt-6 rounded-full" onClick={onClose} data-testid="blvd-close-error">Close</Button>
        </>
      )}

      {step === "service" && (
        <>
          <Header title="Choose a service" sub={locationName ? `${locationName} · real-time availability` : "Real-time availability powered by Boulevard."} />
          <div className="space-y-2">
            {items.map((it) => (
              <button
                key={it.id}
                disabled={busy}
                onClick={() => chooseItem(it)}
                data-testid={`blvd-service-${it.id}`}
                className="w-full text-left rounded-xl border border-stone-200 hover:border-iri-mid hover:bg-stone/50 px-4 py-3 transition-all disabled:opacity-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-ink font-medium">{it.name}</div>
                    {it.category && <div className="text-xs text-[#8A9492]">{it.category}</div>}
                  </div>
                  {it.price && <span className="text-sm text-[#5C6664] whitespace-nowrap">{it.price}</span>}
                </div>
              </button>
            ))}
            {!items.length && <p className="text-sm text-[#5C6664]">No services are available for online booking.</p>}
          </div>
        </>
      )}

      {step === "provider" && (
        <>
          <Header title="Choose your provider" sub={selectedItem?.name} back={() => setStep("service")} />
          <div className="space-y-2">
            <button
              disabled={busy}
              onClick={() => chooseProvider(null)}
              data-testid="blvd-provider-any"
              className="w-full text-left rounded-xl border border-stone-200 hover:border-iri-mid hover:bg-stone/50 px-4 py-3 transition-all disabled:opacity-50"
            >
              <div className="text-ink font-medium">No preference</div>
              <div className="text-xs text-[#8A9492]">First available provider</div>
            </button>
            {providers.map((v) => (
              <button
                key={v.id}
                disabled={busy}
                onClick={() => chooseProvider(v)}
                data-testid={`blvd-provider-${v.id}`}
                className="w-full text-left rounded-xl border border-stone-200 hover:border-iri-mid hover:bg-stone/50 px-4 py-3 transition-all disabled:opacity-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-iri-mid" />
                    <span className="text-ink font-medium">{staffName(v)}</span>
                  </div>
                  {money(v.price) && <span className="text-sm text-[#5C6664] whitespace-nowrap">{money(v.price)}</span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {step === "datetime" && (
        <>
          <Header
            title="Pick a date & time"
            sub={[selectedItem?.name, selectedProvider ? staffName(selectedProvider) : (providers.length > 1 ? "No preference" : null)].filter(Boolean).join(" · ")}
            back={() => setStep(providers.length > 1 ? "provider" : "service")}
          />
          <div className="flex items-center gap-2 text-xs font-medium text-[#8A9492] uppercase tracking-wide mb-2">
            <Calendar className="h-3.5 w-3.5" /> Date
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dates.map((d) => (
              <button
                key={d.date}
                disabled={busy}
                onClick={() => chooseDate(d)}
                data-testid={`blvd-date-${d.date}`}
                className={`shrink-0 rounded-lg px-3 py-2 text-sm border transition-all ${
                  selectedDate?.date === d.date ? "iri-gradient text-ink border-transparent" : "border-stone-200 text-[#5C6664] hover:border-iri-mid"
                }`}
              >
                {fmtDate(d.date)}
              </button>
            ))}
            {!dates.length && <p className="text-sm text-[#5C6664]">No open dates in the next week.</p>}
          </div>

          {selectedDate && (
            <>
              <div className="flex items-center gap-2 text-xs font-medium text-[#8A9492] uppercase tracking-wide mt-5 mb-2">
                <Clock className="h-3.5 w-3.5" /> Time
              </div>
              {busy ? (
                <Spinner />
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {times.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTime(t)}
                      data-testid={`blvd-time-${t.id}`}
                      className={`rounded-lg px-2 py-2 text-sm border transition-all ${
                        selectedTime?.id === t.id ? "iri-gradient text-ink border-transparent" : "border-stone-200 text-[#5C6664] hover:border-iri-mid"
                      }`}
                    >
                      {fmtTime(t.startTime)}
                    </button>
                  ))}
                  {!times.length && <p className="col-span-full text-sm text-[#5C6664]">No open times for this day.</p>}
                </div>
              )}
            </>
          )}

          <Button
            className="w-full iri-gradient text-ink font-medium rounded-full h-12 mt-6 hover:opacity-90 disabled:opacity-50"
            disabled={!selectedTime || busy}
            onClick={goToDetails}
            data-testid="blvd-continue-details"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
          </Button>
        </>
      )}

      {step === "details" && (
        <form onSubmit={submitDetails}>
          <Header
            title="Your details"
            sub={[selectedItem?.name, selectedProvider ? staffName(selectedProvider) : null, `${fmtDate(selectedDate?.date)} · ${fmtTime(selectedTime?.startTime)}`].filter(Boolean).join(" · ")}
            back={() => setStep("datetime")}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First name</Label>
              <Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="blvd-first-name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Last name</Label>
              <Input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="blvd-last-name" className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="blvd-email" className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Phone</Label>
              <Input required value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} placeholder="+1 586 555 0100" data-testid="blvd-phone" className="mt-1" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full iri-gradient text-ink font-medium rounded-full h-12 mt-6 hover:opacity-90 disabled:opacity-50" data-testid="blvd-submit-details">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
          </Button>
        </form>
      )}

      {step === "payment" && (
        <form onSubmit={submitPayment}>
          <Header title="Secure your booking" sub="A card is required to hold this appointment." back={() => setStep("details")} />
          <div className="flex items-center gap-2 text-xs text-[#8A9492] mb-3">
            <CreditCard className="h-4 w-4 text-iri-mid" /> Card details are sent directly to Boulevard.
          </div>
          <div className="rounded-xl bg-stone/70 border border-stone-200 p-4 mb-4" data-testid="blvd-voucher-note">
            <p className="text-sm text-ink font-medium">Have a package or treatment vouchers?</p>
            <p className="text-sm text-[#5C6664] leading-relaxed mt-1">
              We keep a card on file to reserve your appointment — <span className="text-ink">you won't be charged today</span>.
              If you've purchased a package or have treatment vouchers on your account, they'll be applied at your visit,
              leaving <span className="text-ink font-medium">no balance due</span>.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Name on card</Label>
              <Input required value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} data-testid="blvd-card-name" className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Card number</Label>
              <Input required value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="4242 4242 4242 4242" data-testid="blvd-card-number" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Exp month</Label>
              <Input required value={card.exp_month} onChange={(e) => setCard({ ...card, exp_month: e.target.value })} placeholder="MM" data-testid="blvd-card-exp-month" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Exp year</Label>
              <Input required value={card.exp_year} onChange={(e) => setCard({ ...card, exp_year: e.target.value })} placeholder="YYYY" data-testid="blvd-card-exp-year" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">CVV</Label>
              <Input required value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} placeholder="123" data-testid="blvd-card-cvv" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">ZIP</Label>
              <Input required value={card.address_postal_code} onChange={(e) => setCard({ ...card, address_postal_code: e.target.value })} placeholder="48038" data-testid="blvd-card-zip" className="mt-1" />
            </div>
          </div>
          {money(summary?.total) && (
            <p className="text-xs text-[#8A9492] mt-3">Estimated total: <span className="text-ink font-medium">{money(summary.total)}</span></p>
          )}
          <Button type="submit" disabled={busy} className="w-full iri-gradient text-ink font-medium rounded-full h-12 mt-6 hover:opacity-90 disabled:opacity-50" data-testid="blvd-confirm-booking">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm booking"}
          </Button>
        </form>
      )}

      {step === "done" && (
        <div className="text-center py-6" data-testid="blvd-success">
          <div className="mx-auto h-14 w-14 rounded-full iri-gradient flex items-center justify-center mb-4">
            <Check className="h-7 w-7 text-ink" />
          </div>
          <h3 className="font-heading text-2xl font-light text-ink">You're booked!</h3>
          <p className="text-sm text-[#5C6664] mt-2">
            {selectedItem?.name} on {fmtDate(selectedDate?.date)} at {fmtTime(selectedTime?.startTime)}.
          </p>
          {locationName && (
            <p className="text-sm text-[#5C6664] mt-1 flex items-center justify-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-iri-mid" /> {locationName}
            </p>
          )}
          <p className="text-xs text-[#8A9492] mt-3">A confirmation has been sent to {form.email}.</p>
          <Button className="mt-6 rounded-full iri-gradient text-ink" onClick={onClose} data-testid="blvd-done-close">Done</Button>
        </div>
      )}
    </div>
  );
}
